import { hasClass } from '@utility/class-name';
import { cn } from '@skupreme/shared/ui';
import type { FieldWidth } from 'types/common/form-fields';

/*
 * The kit's input look (`TextField` in @skupreme/shared/ui), for the bare
 * `<input>`s these fields render so callers' input props still reach the DOM.
 *
 * Most callers still wrap these fields in a Semantic `<Form>`, whose
 * `.ui.form input[type=…]` rules outrank a single utility class. Every
 * property those rules set therefore carries `!` until the forms move off
 * Semantic.
 */
export const FIELD_INPUT = cn(
  'sk:m-0! sk:box-border sk:w-full sk:min-w-0 sk:appearance-none sk:outline-none!',
  'sk:h-7! sk:rounded-full! sk:border-0! sk:px-3! sk:py-0!',
  'sk:font-sans! sk:text-dense! sk:leading-5! sk:font-medium sk:tracking-dense sk:text-fg!',
  'sk:bg-surface! sk:inset-shadow-hairline! sk:shadow-card!',
  'sk:transition-[box-shadow] sk:duration-75',
  'sk:placeholder:font-normal sk:placeholder:text-fg-tertiary!',
  'sk:focus:shadow-none!',
  'sk:disabled:cursor-not-allowed sk:disabled:opacity-50',
);

/** `TextAreaField`'s card, with the same `!` reasoning as `FIELD_INPUT`. */
export const FIELD_TEXTAREA = cn(
  FIELD_INPUT,
  'sk:h-auto! sk:min-h-24 sk:resize-y sk:rounded-card! sk:p-3!',
);

/* A ring rather than a border: it adds to the hairline without moving the text. */
export const FIELD_INPUT_INVALID = 'sk:ring-1 sk:ring-danger';

export const FIELD_LABEL = cn(
  'mute-label sk:text-xs! sk:leading-4! sk:font-normal! sk:text-fg-tertiary!',
  'sk:[&.has-description]:text-sm! sk:[&.has-description]:font-bold! sk:[&.has-description]:text-fg!',
);

export const FIELD_MESSAGE = 'sk:mt-1 sk:block sk:text-xs sk:text-danger';

/*
 * `field` and `<n> wide` are kept for the callers' Semantic `Form.Group`s,
 * which lay out their children by those classes.
 */
export const FIELD_FRAME = 'field sk:relative sk:min-w-0';

/**
 * The old CustomField's 1.5em bottom margin, with `!` because Semantic zeroes
 * a group's last field at (0,3,0). A caller's `mb-0` or `mb-2` escapes it.
 */
export function fieldMargin(className?: string) {
  if (hasClass(className, 'sk:mb-0!')) return 'sk:mb-0!';
  if (hasClass(className, 'sk:mb-2!')) return 'sk:mb-4!';
  return 'sk:mb-[1.5em]!';
}

const WIDTH_WORDS = [
  '',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
];

export function widthClass(width?: FieldWidth): string | undefined {
  if (width === undefined || width === null) {
    return undefined;
  }
  const word =
    typeof width === 'number' || /^\d+$/.test(width)
      ? WIDTH_WORDS[Number(width)]
      : width;
  return word ? `${word} wide` : undefined;
}

/*
 * react-datepicker's calendar, in the kit's popover look. Its class names
 * carry `__`, which Tailwind reads as spaces unless escaped, hence String.raw.
 */
export const DATEPICKER_SCOPE = cn(
  'sk:[&_.react-datepicker-wrapper]:w-full',
  'sk:[&_.react-datepicker]:font-sans! sk:[&_.react-datepicker]:rounded-card! sk:[&_.react-datepicker]:border-0! sk:[&_.react-datepicker]:bg-surface! sk:[&_.react-datepicker]:text-fg! sk:[&_.react-datepicker]:inset-shadow-hairline sk:[&_.react-datepicker]:shadow-card',
  String.raw`sk:[&_.react-datepicker\_\_triangle]:hidden!`,
  String.raw`sk:[&_.react-datepicker\_\_header]:rounded-t-card! sk:[&_.react-datepicker\_\_header]:border-0! sk:[&_.react-datepicker\_\_header]:bg-surface!`,
  String.raw`sk:[&_.react-datepicker\_\_current-month]:text-dense! sk:[&_.react-datepicker\_\_current-month]:font-medium! sk:[&_.react-datepicker\_\_current-month]:text-fg!`,
  String.raw`sk:[&_.react-datepicker\_\_day-name]:text-xs! sk:[&_.react-datepicker\_\_day-name]:text-fg-tertiary!`,
  String.raw`sk:[&_.react-datepicker\_\_day]:rounded-full! sk:[&_.react-datepicker\_\_day]:text-dense! sk:[&_.react-datepicker\_\_day]:text-fg!`,
  String.raw`sk:[&_.react-datepicker\_\_day:hover]:bg-fill-secondary!`,
  String.raw`sk:[&_.react-datepicker\_\_day--disabled]:text-fg-tertiary! sk:[&_.react-datepicker\_\_day--outside-month]:text-fg-tertiary!`,
  String.raw`sk:[&_.react-datepicker\_\_day--in-range]:bg-fill-secondary! sk:[&_.react-datepicker\_\_day--in-selecting-range]:bg-fill-secondary!`,
  String.raw`sk:[&_.react-datepicker\_\_day--selected]:bg-primary! sk:[&_.react-datepicker\_\_day--selected]:text-surface!`,
  String.raw`sk:[&_.react-datepicker\_\_day--range-start]:bg-primary! sk:[&_.react-datepicker\_\_day--range-start]:text-surface!`,
  String.raw`sk:[&_.react-datepicker\_\_day--range-end]:bg-primary! sk:[&_.react-datepicker\_\_day--range-end]:text-surface!`,
  String.raw`sk:[&_.react-datepicker\_\_day--keyboard-selected]:bg-fill-secondary!`,
);
